/**
 * Shared vocabulary linking the requisition's structured test keys to LOINC.
 *
 * Today a lab checkbox on a requisition is filled by matching the field's path
 * segment against the order's DISPLAY TERMS with a hardcoded regex table
 * (`STRUCTURED_TEST_PATTERNS` in the artifact runtime). That is brittle in
 * three ways: a clinic's own form gets nothing until someone edits application
 * code, "Na" versus "Sodium, serum" is a coin flip, and a test the table has
 * never heard of silently spills into the free-text overflow instead of
 * ticking its own box. Codes replace the guessing: a field binding carries the
 * test it IS, and the order carries the tests it asked for, so the match is an
 * identity comparison.
 *
 * PROVENANCE. Codes here were supplied by the clinical owner against the
 * Ontario 4422 requisition, not derived by this codebase. A wrong code does not
 * fail loudly — it binds a returned result to the wrong ordered test — so they
 * are owner-authored on purpose, and any change to one is a clinical change.
 *
 * An entry with `loinc: null` is not a gap to be filled by guesswork. It means
 * the test keeps today's exact behaviour: the fuzzy display-term match still
 * fills its checkbox, and the checkbox is not clinician-clickable. That is the
 * safe default, and it is why codes can be added one verified test at a time
 * with no regression in between.
 *
 * ORDER CODE vs RESULT CODE. Several of these name what is ORDERED (a panel or
 * a study). A laboratory frequently reports results under a different, narrower
 * code than the one used to order — a lipid panel returns individual analytes.
 * Results matching must therefore allow a result to bind through the order it
 * belongs to, not by assuming the returned code equals the ordered code.
 */
/**
 * Keys mirror `STRUCTURED_TEST_PATTERNS` exactly, except code-only entries; a drift test in
 * agent-api fails if the two ever diverge.
 */
export const LAB_TEST_CATALOGUE = [
    { key: "glucose", display: "Glucose", loinc: "2345-7" },
    { key: "hba1c", display: "Hemoglobin A1c", loinc: "4548-4" },
    { key: "creatinine_egfr", display: "Creatinine and eGFR", loinc: "2160-0" },
    { key: "uric_acid", display: "Uric acid", loinc: "3084-1" },
    { key: "sodium", display: "Sodium", loinc: "2951-2" },
    { key: "potassium", display: "Potassium", loinc: "2823-3" },
    { key: "alt", display: "Alanine aminotransferase (ALT)", loinc: "1742-6" },
    { key: "alkaline_phosphatase", display: "Alkaline phosphatase", loinc: "6768-6" },
    { key: "bilirubin", display: "Bilirubin, total", loinc: "1975-2" },
    { key: "albumin", display: "Albumin", loinc: "1751-7" },
    { key: "lipid_assessment", display: "Lipid assessment", loinc: "100898-6" },
    {
        key: "urine_albumin_creatinine_ratio",
        display: "Urine albumin/creatinine ratio",
        loinc: "32294-1",
    },
    { key: "urinalysis_chemical", display: "Urinalysis, chemical", loinc: "50556-0" },
    { key: "cbc", display: "Complete blood count (CBC)", loinc: "58410-2" },
    { key: "inr", display: "INR", loinc: "6301-6" },
    { key: "pregnancy_test_urine", display: "Pregnancy test, urine", loinc: "110413-2" },
    { key: "mononucleosis_screen", display: "Mononucleosis screen", loinc: "111333-1" },
    { key: "rubella", display: "Rubella", loinc: "85701-1" },
    {
        key: "prenatal_abo_rhd_antibody_screen",
        display: "Prenatal ABO/RhD and antibody screen",
        loinc: "54060-9",
    },
    { key: "repeat_prenatal_antibodies", display: "Repeat prenatal antibodies", loinc: "100230-2" },
    { key: "microbiology.cervical", display: "Cervical culture", loinc: "43410-0" },
    { key: "microbiology.vaginal", display: "Vaginal culture", loinc: "11261-5" },
    { key: "group_b_strep", display: "Group B streptococcus", loinc: "58713-9" },
    // Aliases name only Chlamydia trachomatis and Neisseria gonorrhoeae (the owner's STI set):
    // "Chlamydia pneumoniae" and "Chlamydia serology" are other tests, so neither is an alias.
    {
        key: "chlamydia",
        display: "Chlamydia",
        loinc: "108416-9",
        aliases: ["chlamydia naat", "chlamydia trachomatis", "chlamydia trachomatis naat", "ct naat"],
    },
    {
        key: "gonorrhea",
        display: "Gonorrhea",
        loinc: "60256-5",
        aliases: [
            "gonorrhoea",
            "gc",
            "gc naat",
            "gonorrhea naat",
            "gonorrhoea naat",
            "neisseria gonorrhoeae",
            "neisseria gonorrhoeae naat",
        ],
    },
    { key: "sputum_culture", display: "Sputum culture", loinc: "88837-0" },
    { key: "throat_culture", display: "Throat culture", loinc: "17898-8" },
    { key: "wound_culture", display: "Wound culture", loinc: "6462-6" },
    { key: "urine_culture", display: "Urine culture", loinc: "100904-2" },
    { key: "stool_culture", display: "Stool culture", loinc: "82305-4" },
    { key: "stool_ova_and_parasites", display: "Stool ova and parasites", loinc: "10701-1" },
    // The published 4422 binds its Viral Hepatitis "Immune Status / Previous Exposure"
    // Hepatitis A, B and C boxes to these keys; any other hepatitis test is written
    // in "Other tests". The code lets the note and a returned result find the test.
    { key: "hepatitis_a", display: "Hepatitis A", loinc: "53775-3" },
    {
        // The Hepatitis B immune status box is the anti-HBs immunity test (owner decision
        // 2026-09-27; it was 109333-5, an anti-HBc order term). Code-only: the box is ticked by the
        // coded test through its binding, never by wording, so a free-text "Hepatitis B ..." order
        // (surface antigen, core antibody, viral load) no longer ticks it and is written on the text
        // line instead. A bare "hepatitis b" is deliberately not a name of this test.
        key: "hepatitis_b",
        display: "Hepatitis B surface antibody (anti-HBs), immunity",
        loinc: "16935-9",
        codeOnly: true,
        aliases: ["anti-hbs", "hbsab", "hep b immunity", "hepatitis b immunity"],
    },
    { key: "hepatitis_c", display: "Hepatitis C", loinc: "16128-1" },
    { key: "total_psa", display: "Total PSA", loinc: "110671-5" },
    { key: "free_psa", display: "Free PSA", loinc: "110243-3" },
    // No box on the 4422 or PHO General: written in "Other tests" / "Test requested". Code-only.
    {
        // 56888-1 is the HIV-1/2 antibody plus HIV-1 p24 antigen immunoassay (the fourth-generation
        // screen), the orderable test; 75622-1 is a US Meaningful Use grouping and not orderable. An
        // antibody-only HIV test is a different order and deliberately not an alias. The display stays
        // exactly "HIV": clinic rules and order-set line text match that word.
        key: "hiv",
        display: "HIV",
        loinc: "56888-1",
        codeOnly: true,
        aliases: ["hiv serology", "hiv screen"],
    },
    {
        // 47236-5 is the treponemal IgG+IgM antibody screen (treponemal-first screening). RPR
        // (20507-0) and VDRL (5292-8) are different, non-treponemal tests, so neither is an alias.
        // The display stays exactly "Syphilis": clinic rules and order-set line text match that word.
        key: "syphilis",
        display: "Syphilis",
        loinc: "47236-5",
        codeOnly: true,
        aliases: ["syphilis serology", "syphilis screen", "treponemal screen"],
    },
    // Owner-supplied 2026-09-27 for the prenatal order set. Code-only: matched by code alone. The
    // aliases name only the quantitative (beta) serum test; an unqualified "hCG" or "human chorionic
    // gonadotropin" could be the urine or qualitative pregnancy test, so neither is an alias.
    {
        key: "hcg_quantitative",
        display: "hCG, quantitative (serum)",
        loinc: "19080-1",
        codeOnly: true,
        aliases: [
            "beta hcg",
            "b-hcg",
            "β-hcg",
            "bhcg",
            "serum hcg",
            "serum beta hcg",
            "serum b-hcg",
            "serum β-hcg",
            "quantitative hcg",
            "hcg quantitative",
            "hcg, quantitative",
            "quantitative serum hcg",
            "quantitative serum beta hcg",
            "quantitative serum b-hcg",
            "quantitative serum β-hcg",
            "quantitative beta hcg",
            "quantitative b-hcg",
            "quantitative β-hcg",
        ],
    },
    {
        key: "hepatitis_b_surface_antigen",
        display: "Hepatitis B surface antigen (HBsAg)",
        loinc: "5196-1",
        codeOnly: true,
        aliases: [
            "hbsag",
            "hepatitis b surface antigen",
            "hep b surface antigen",
            "hepatitis b s antigen",
            "hbs antigen",
        ],
    },
    // Owner-reviewed expansion 2026-09-27. All code-only: none has a box pattern, so each is written
    // on a text line ("Other tests", "Test requested") unless a form binds a box to its key, and a
    // bound box is ticked by the coded test alone. Codes are the owner's (SI-unit codes where Ontario
    // labs report in SI units). Aliases follow the conservative rule above.
    {
        key: "tsh",
        display: "Thyroid-stimulating hormone (TSH)",
        loinc: "3016-3",
        codeOnly: true,
        aliases: ["tsh", "thyrotropin"],
    },
    {
        key: "free_t4",
        display: "Free T4",
        loinc: "14920-3",
        codeOnly: true,
        aliases: ["ft4", "free thyroxine"],
    },
    { key: "ferritin", display: "Ferritin", loinc: "2276-4", codeOnly: true },
    {
        key: "vitamin_b12",
        display: "Vitamin B12",
        loinc: "14685-2",
        codeOnly: true,
        aliases: ["b12", "cobalamin"],
    },
    {
        key: "c_reactive_protein",
        display: "C-reactive protein (CRP)",
        loinc: "1988-5",
        codeOnly: true,
        aliases: ["crp"],
    },
    {
        key: "esr",
        display: "Erythrocyte sedimentation rate (ESR)",
        loinc: "30341-2",
        codeOnly: true,
        aliases: ["esr", "sed rate"],
    },
    {
        key: "electrolytes",
        display: "Electrolytes (Na, K, Cl, total CO2)",
        loinc: "24326-1",
        codeOnly: true,
        aliases: ["lytes", "electrolytes"],
    },
    { key: "chloride", display: "Chloride", loinc: "2075-0", codeOnly: true, aliases: ["cl"] },
    {
        key: "total_co2",
        display: "Bicarbonate (total CO2)",
        loinc: "2028-9",
        codeOnly: true,
        aliases: ["total co2", "bicarbonate"],
    },
    { key: "urea", display: "Urea", loinc: "22664-7", codeOnly: true },
    { key: "calcium", display: "Calcium", loinc: "2000-8", codeOnly: true },
    { key: "magnesium", display: "Magnesium", loinc: "2601-3", codeOnly: true, aliases: ["mg"] },
    {
        key: "phosphate",
        display: "Phosphate",
        loinc: "14879-1",
        codeOnly: true,
        aliases: ["phosphorus"],
    },
    {
        key: "ast",
        display: "Aspartate aminotransferase (AST)",
        loinc: "1920-8",
        codeOnly: true,
        aliases: ["ast", "sgot"],
    },
    {
        key: "ggt",
        display: "Gamma-glutamyl transferase (GGT)",
        loinc: "2324-2",
        codeOnly: true,
        aliases: ["ggt"],
    },
    { key: "lipase", display: "Lipase", loinc: "3040-3", codeOnly: true },
    {
        key: "creatine_kinase",
        display: "Creatine kinase (CK)",
        loinc: "2157-6",
        codeOnly: true,
        aliases: ["ck", "cpk"],
    },
    { key: "nt_probnp", display: "NT-proBNP", loinc: "33762-6", codeOnly: true, aliases: ["probnp"] },
    {
        key: "iron_panel",
        display: "Iron panel (iron, TIBC, saturation)",
        loinc: "50190-8",
        codeOnly: true,
        aliases: ["iron panel", "iron and tibc"],
    },
    {
        key: "apob",
        display: "Apolipoprotein B (ApoB)",
        loinc: "1884-6",
        codeOnly: true,
        aliases: ["apob"],
    },
    {
        key: "lipoprotein_a",
        display: "Lipoprotein(a)",
        loinc: "43583-4",
        codeOnly: true,
        aliases: ["lp(a)"],
    },
    {
        key: "testosterone_total",
        display: "Testosterone, total",
        loinc: "14913-8",
        codeOnly: true,
        aliases: ["testosterone"],
    },
    {
        key: "fsh",
        display: "Follicle-stimulating hormone (FSH)",
        loinc: "15067-2",
        codeOnly: true,
        aliases: ["fsh"],
    },
    { key: "prolactin", display: "Prolactin", loinc: "2842-3", codeOnly: true },
    {
        key: "pth",
        display: "Parathyroid hormone (PTH)",
        loinc: "14866-8",
        codeOnly: true,
        aliases: ["pth"],
    },
    {
        key: "ttg_iga",
        display: "Tissue transglutaminase IgA (celiac screen)",
        loinc: "31017-7",
        codeOnly: true,
        aliases: ["ttg", "celiac screen"],
    },
    {
        key: "h_pylori_stool_antigen",
        display: "H. pylori stool antigen",
        loinc: "17780-8",
        codeOnly: true,
    },
    {
        key: "c_difficile_toxin",
        display: "C. difficile toxin",
        loinc: "34713-8",
        codeOnly: true,
        aliases: ["c diff", "c. difficile"],
    },
    {
        key: "gestational_diabetes_screen_50g",
        display: "Gestational diabetes screen, 50 g (1 hour)",
        loinc: "1504-0",
        codeOnly: true,
        aliases: ["gct"],
    },
    {
        key: "gestational_diabetes_ogtt_75g",
        display: "Gestational diabetes 75 g OGTT (2 hour)",
        loinc: "81324-6",
        codeOnly: true,
        fasting: "required",
        aliases: ["75 g ogtt", "2 hour gtt"],
    },
    {
        key: "tb_igra",
        display: "TB interferon-gamma release assay (QuantiFERON)",
        loinc: "71773-6",
        codeOnly: true,
        aliases: ["quantiferon", "igra"],
    },
    {
        key: "urine_drug_screen",
        display: "Urine drug screen",
        loinc: "69739-1",
        codeOnly: true,
        aliases: ["uds"],
    },
    {
        key: "post_vasectomy_semen_analysis",
        display: "Post-vasectomy semen analysis",
        loinc: "29990-9",
        codeOnly: true,
    },
    {
        key: "fungal_culture",
        display: "Fungal culture (skin, nail, hair)",
        loinc: "580-1",
        codeOnly: true,
        aliases: ["fungal culture"],
    },
    {
        key: "trichomonas_naat",
        display: "Trichomonas NAAT",
        loinc: "46154-1",
        codeOnly: true,
        aliases: ["tv naat"],
    },
    {
        key: "aptt",
        display: "Partial thromboplastin time (aPTT)",
        loinc: "14979-9",
        codeOnly: true,
        aliases: ["aptt", "ptt"],
    },
    { key: "d_dimer", display: "D-dimer", loinc: "48065-7", codeOnly: true },
    {
        key: "vitamin_d_25_oh",
        display: "Vitamin D, 25-hydroxy",
        loinc: "68438-1",
        codeOnly: true,
        aliases: ["vitamin d", "25-oh vitamin d"],
    },
    { key: "lithium_level", display: "Lithium level", loinc: "14334-7", codeOnly: true },
    { key: "digoxin_level", display: "Digoxin level", loinc: "10535-3", codeOnly: true },
    {
        key: "valproic_acid_level",
        display: "Valproic acid level",
        loinc: "4086-5",
        codeOnly: true,
        aliases: ["valproate level"],
    },
    {
        key: "carbamazepine_level",
        display: "Carbamazepine level",
        loinc: "3432-2",
        codeOnly: true,
        aliases: ["tegretol level"],
    },
    {
        key: "phenytoin_level",
        display: "Phenytoin level",
        loinc: "3968-5",
        codeOnly: true,
        aliases: ["dilantin level"],
    },
    {
        key: "other_swab_culture",
        display: "Culture, other swab or pus (source given)",
        loinc: "6463-4",
        codeOnly: true,
        aliases: ["ear swab", "eye swab"],
    },
    {
        key: "hepatitis_b_core_antibody",
        display: "Hepatitis B core antibody, total (anti-HBc)",
        loinc: "16933-4",
        codeOnly: true,
        aliases: ["anti-hbc"],
    },
    {
        key: "measles_igg",
        display: "Measles IgG (immunity)",
        loinc: "20479-2",
        codeOnly: true,
        aliases: ["measles immunity", "measles igg"],
    },
    {
        key: "mumps_igg",
        display: "Mumps IgG (immunity)",
        loinc: "22415-4",
        codeOnly: true,
        aliases: ["mumps immunity", "mumps igg"],
    },
    {
        key: "varicella_igg",
        display: "Varicella zoster IgG (immunity)",
        loinc: "19162-7",
        codeOnly: true,
        aliases: ["varicella immunity", "vzv igg"],
    },
    {
        key: "lyme",
        display: "Lyme disease serology",
        loinc: "20449-5",
        codeOnly: true,
        aliases: ["lyme serology"],
    },
    {
        key: "hsv_pcr",
        display: "Herpes simplex virus PCR (lesion swab)",
        loinc: "20444-6",
        codeOnly: true,
        aliases: ["hsv pcr"],
    },
    {
        key: "pertussis",
        display: "Bordetella pertussis PCR",
        loinc: "43913-3",
        codeOnly: true,
        aliases: ["pertussis pcr", "whooping cough swab"],
    },
    {
        key: "respiratory_virus_panel",
        display: "Respiratory virus panel (influenza A/B, RSV, COVID-19)",
        loinc: "95941-1",
        codeOnly: true,
        aliases: ["flu/covid/rsv swab"],
    },
];
export const labTestCatalogueByKey = new Map(LAB_TEST_CATALOGUE.map((entry) => [entry.key, entry]));
/** Only coded entries are reachable by code, so this map skips the nulls. */
export const labTestCatalogueByLoinc = new Map(LAB_TEST_CATALOGUE.filter((entry) => entry.loinc !== null).map((entry) => [entry.loinc, entry]));
/** Lower-cased, whitespace-collapsed form of a test name, as aliases are written. */
export function normalizeLabTestName(name) {
    return name.normalize("NFKC").toLowerCase().replace(/\s+/g, " ").trim();
}
/** Every name an entry answers to: its display plus its aliases, normalized. */
export function labTestCatalogueAliases(entry) {
    return [
        ...new Set([entry.display, ...(entry.aliases ?? [])].map((name) => normalizeLabTestName(name))),
    ];
}
/**
 * Catalogue entries by normalized display or alias: an exact-lookup helper for whole names, never a
 * substring or containment match. It does not decide whether a free-text test is one of a clinic
 * order set's tests; that matcher (`@emr/domain` form-order-set-orders) applies its own stricter
 * rules, which also check the sample.
 */
export const labTestCatalogueByName = new Map(LAB_TEST_CATALOGUE.flatMap((entry) => labTestCatalogueAliases(entry).map((name) => [name, entry])));
/** Comparison key for a coded test. Trimmed and upper-cased so casing never splits a match. */
export function labTestCodeKey(codeSystem, code) {
    return `${codeSystem.trim().toUpperCase()}|${code.trim().toUpperCase()}`;
}
/** The `structured_order.tests.` prefix that names a test binding's path. */
export const STRUCTURED_TEST_PATH_PREFIX = "structured_order.tests.";
/**
 * Other `structured_order.tests.<key>` names a clinic's published requisition gives a catalogue
 * test, each mapped to exactly one catalogue key. The same test and code under the form's own
 * wording, never a new test or code: the owner's Ontario Lab Requisition binds its "Other swabs /
 * pus culture" box as `other_swab_or_pus`, which is `other_swab_culture` (6463-4).
 */
export const LAB_TEST_KEY_ALIASES = new Map([
    ["other_swab_or_pus", "other_swab_culture"],
]);
/**
 * The catalogue test a `structured_order.tests.<key>` path names, by its catalogue key or an
 * accepted alias of it, or undefined for any other path. A sub-path such as
 * `<key>.specimen_source` names no test.
 */
export function labTestCatalogueEntryForPath(path) {
    if (path === undefined || !path.startsWith(STRUCTURED_TEST_PATH_PREFIX))
        return undefined;
    const key = path.slice(STRUCTURED_TEST_PATH_PREFIX.length);
    return labTestCatalogueByKey.get(LAB_TEST_KEY_ALIASES.get(key) ?? key);
}
/**
 * The test a requisition checkbox resolves to.
 *
 * An explicit `orderCode` always wins; otherwise the code is derived from the
 * binding's reviewed source path through the catalogue. Both halves are
 * human-vetted — the path was reviewed when the form was published, and the
 * codes are owner-supplied — so this is a lookup between verified facts.
 *
 * Deliberately lives in contracts so the renderer and the encounter UI resolve
 * a box to a test through the SAME function. If they diverged, the form could
 * show a clinician one thing while the order carried another.
 */
export function labTestCodeForBinding(binding) {
    if (binding.orderCode !== undefined)
        return binding.orderCode;
    const loinc = labTestCatalogueEntryForPath(binding.sourcePath)?.loinc;
    // A catalogue entry left uncoded derives nothing: it keeps the legacy match
    // and its box stays non-clickable, rather than inventing an identity.
    return loinc === null || loinc === undefined ? undefined : { code: loinc, codeSystem: "LOINC" };
}
/**
 * Resolves a field binding to the ONE laboratory test its checkbox orders, or
 * undefined when the box is not an unambiguous order input.
 *
 * A tick on such a box changes the ORDER; it never edits the PDF. That is why
 * this is deliberately not an exception to the authoritative-field rule: the
 * value printed on the paper still comes from the order, and the requisition is
 * re-rendered from the amended order rather than scribbled on.
 *
 * `authoritative` / `readOnlyAtRuntime` are NOT relaxed. The contract requires
 * authoritative fields to be read-only, so "editable structured-order checkbox"
 * cannot be expressed as a binding at all; the carve-out lives here, in code,
 * where it is one function that the renderer and the encounter UI share. Two
 * copies of this rule could disagree, and a disagreement would show a clinician
 * a ticked box for a test the order does not contain.
 */
export function orderEditableLabTestForBinding(field) {
    // Checkbox only, never radio. Ticking one radio silently unticks its group
    // sibling, and the browser fires no event on the sibling — so the order would
    // gain a test and never lose the one it replaced. A radio test box therefore
    // keeps today's behaviour: filled from the order, not clinician-clickable.
    if (field.reviewStatus !== "reviewed" ||
        field.source.kind !== "structured_order" ||
        field.fieldType !== "checkbox") {
        return undefined;
    }
    const path = field.source.path;
    const resolved = labTestCodeForBinding({ sourcePath: path, orderCode: field.orderCode });
    if (resolved === undefined)
        return undefined;
    const display = field.orderCode?.display ?? labTestCatalogueEntryForPath(path)?.display;
    // A code with no name cannot be written into an order line a human reads, so
    // the box stays inert rather than ordering something unnameable.
    if (display === undefined)
        return undefined;
    return {
        code: resolved.code,
        codeSystem: resolved.codeSystem,
        display,
        ...(path === undefined ? {} : { sourcePath: path }),
    };
}
/** The longest test name a clinician may type on an "Other tests" line: an order's code limit. */
export const TYPED_LAB_TEST_MAX_LENGTH = 80;
/** A typed test name as an order carries it: spacing collapsed, closing punctuation dropped. */
export function typedLabTestName(text) {
    return text
        .replace(/\s+/g, " ")
        .replace(/[\s.,;:]+$/u, "")
        .trim();
}
/**
 * The test a name typed on a requisition's "Other tests" line orders: the catalogue's coded test
 * when the name is exactly one of its names (its display or an alias, compared as a whole name
 * after normalizing case and spacing, so "TSH" is the coded TSH), otherwise a free-text test that
 * keeps the words as typed, so it still prints. Undefined for a blank name, or one longer than an
 * order's test code allows.
 */
export function typedLabTest(text) {
    const name = typedLabTestName(text);
    if (name.length === 0 || name.length > TYPED_LAB_TEST_MAX_LENGTH)
        return undefined;
    const entry = labTestCatalogueByName.get(normalizeLabTestName(name));
    if (entry !== undefined && entry.loinc !== null) {
        return {
            code: entry.loinc,
            codeSystem: "LOINC",
            display: entry.display,
            sourcePath: `${STRUCTURED_TEST_PATH_PREFIX}${entry.key}`,
        };
    }
    return { code: name, codeSystem: "free_text", display: name };
}
/** True when a clinician may tick this box to add or remove its test. */
/** Field types a clinician may type into on a rendered requisition. */
export const RUNTIME_EDITABLE_CLINIC_DOCUMENT_FIELD_TYPES = new Set([
    "text",
    "multiline_text",
    "checkbox",
    "radio",
    "dropdown",
    "date",
]);
/**
 * A reviewed manual-input binding the clinic published as editable at
 * runtime. Authoritative and read-only bindings never qualify, whatever their
 * widget type.
 */
export function isRuntimeEditableClinicDocumentField(field) {
    return (field.reviewStatus === "reviewed" &&
        field.source.kind === "manual_document_input" &&
        !field.authoritative &&
        !field.readOnlyAtRuntime &&
        RUNTIME_EDITABLE_CLINIC_DOCUMENT_FIELD_TYPES.has(field.fieldType));
}
export function isOrderEditableClinicDocumentField(field) {
    return orderEditableLabTestForBinding(field) !== undefined;
}
/** The source path of a requisition's n-th "Other tests" line. */
export const ORDER_LINE_SOURCE_PATH = /^structured_order\.other_tests\[(\d+)]$/;
/**
 * True for an "Other tests" line a clinician may type a test on to change the order: a reviewed
 * text binding of `structured_order.other_tests[n]` that the form uses.
 *
 * Like a test box, the line is order intent, never a PDF edit. The renderer writes the n-th
 * ordered test no box ticked on line n, so what a clinician types there is read as adding,
 * swapping or removing a test, and the requisition is re-rendered from the amended order. That is
 * why the binding's authoritative and read-only flags stay exactly as published.
 */
export function isOrderLineClinicDocumentField(field) {
    return (field.reviewStatus === "reviewed" &&
        field.disposition !== "unused" &&
        field.source.kind === "structured_order" &&
        (field.fieldType === "text" || field.fieldType === "multiline_text") &&
        ORDER_LINE_SOURCE_PATH.test(field.source.path ?? ""));
}
//# sourceMappingURL=lab-test-catalogue.js.map